import { KnownSettingsNodeDtoStatus, KnownSettingsNodeDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Permissions } from '../../../core/permissions/permissions.typings';
import { makeDefineActions } from '../../../ui/components/pure/actions-button/actions-button.helpers';
import { ActionsButtonItem } from '../../../ui/components/pure/actions-button/actions-button.types';
import { Add } from '../../../ui/components/pure/icons/Add.icon';
import { ActionDelete } from '../../../ui/components/pure/icons/ActionDelete.icon';
import { AddRegistrationPage } from '../../../ui/components/pure/icons/AddRegistrationPage.icon';
import { Deleted } from '../../../ui/components/pure/icons/Deleted.icon';
import { RegistrationPageLink } from '../../../ui/components/pure/icons/RegistrationPageLink.icon';
import {
  EnrichedSettingsNodeDto,
  EnrichedSettingsTreeDto,
  RelationshipType
} from '../reducer/settings-tree-reducer.types';
import { LockOutlined } from '@mui/icons-material';
import { LinkIcon } from '../../../ui/components/pure/icons/Link.icon';
import { DEFAULT_PROJECT_CAPABILITIES, ProjectCapabilities } from '../logic/project-capabilities';
import { carrierGroupVariant } from '../forms/carrier-group-form/form';

export enum SettingsActionsButtonActions {
  CREATE_PROJECT = 'CREATE_PROJECT',
  ADD_CARRIER = 'ADD_CARRIER',
  ADD_REGION = 'ADD_REGION',
  ASSIGN_NETWORKS = 'ASSIGN_NETWORKS',
  DELETE_PROJECT = 'DELETE_PROJECT',
  DELETE_PROJECT_SUCCESS = 'DELETE_PROJECT_SUCCESS',
  ADD_PROJECT_MODULE = 'ADD_PROJECT_MODULE',
  DELETE_PROJECT_MODULE = 'DELETE_PROJECT_MODULE',
  ADD_HUB = 'ADD_HUB',
  DELETE_HUB = 'DELETE_HUB',
  DELETE_CARRIER = 'DELETE_CARRIER',
  ADD_REGISTRATION_PAGE = 'ADD_REGISTRATION_PAGE',
  DELETE_REGISTRATION_PAGE = 'DELETE_REGISTRATION_PAGE',
  OPEN_REGISTRATION_PAGE = 'OPEN_REGISTRATION_PAGE',
  ADD_ACCESS_CONTROL = 'ADD_ACCESS_CONTROL',
  DELETE_ACCESS_CONTROL = 'DELETE_ACCESS_CONTROL',
  DELETE_RELATIONSHIP = 'DELETE_RELATIONSHIP',
  ADD_NEW_RELATIONSHIP = 'ADD_NEW_RELATIONSHIP',
  APPLY_KIOSK_THEME_PRESET = 'APPLY_KIOSK_THEME_PRESET'
}

function makeSettingsActionsButtonActions(
  selectedNode?: EnrichedSettingsNodeDto,
  projectChildrenSettingsTree?: EnrichedSettingsTreeDto,
  capabilities: ProjectCapabilities = DEFAULT_PROJECT_CAPABILITIES
): ActionsButtonItem<SettingsActionsButtonActions>[] {
  if (!selectedNode) {
    return [];
  }

  if (selectedNode.isPlaceholder) {
    return [];
  }

  if (selectedNode.type === KnownSettingsNodeDtoType.Project) {
    const parentNode = projectChildrenSettingsTree?.nodes.find((n) => n.id === selectedNode.parentId);

    if (
      parentNode &&
      parentNode.type === KnownSettingsNodeDtoType.ProjectLinkFolder &&
      parentNode.entityId.includes('accessed-via')
    ) {
      return [
        {
          title: 'Remove link',
          Icon: () => (
            <Deleted
              style={{
                width: 18,
                height: 18,
                margin: 4,
                color: '#848484'
              }}
            />
          ),
          action: SettingsActionsButtonActions.DELETE_RELATIONSHIP,
          permission: {
            action: Permissions.hubs.update,
            projectID: selectedNode.entityId
          },
          availableUiFeatures: ['internal-groundfloor']
        }
      ];
    }
    return [
      // Servicing hangs off the organisation, not this project, and `carriers:Update` is checked
      // server-side against the carriers anchor.
      ...(capabilities.carrierOrganisationGroupID && capabilities.carriersAnchorProjectID
        ? [
            {
              title: 'Assign networks',
              Icon: LinkIcon,
              action: SettingsActionsButtonActions.ASSIGN_NETWORKS,
              permission: {
                action: Permissions.carriers.update,
                projectID: capabilities.carriersAnchorProjectID
              },
              availableUiFeatures: ['internal-groundfloor']
            }
          ]
        : []),
      // `carriers:Create` is checked server-side against the carriers anchor, not this project,
      // so the permission follows it there.
      ...(capabilities.canAddRegion && capabilities.carriersAnchorProjectID
        ? [
            {
              title: 'Add region project',
              Icon: Add,
              action: SettingsActionsButtonActions.ADD_REGION,
              permission: {
                action: Permissions.carriers.create,
                projectID: capabilities.carriersAnchorProjectID
              },
              availableUiFeatures: ['internal-groundfloor']
            }
          ]
        : []),
      // Only on the project carrier organisations are filed under.
      ...(capabilities.canAddCarrier
        ? [
            {
              title: 'Add carrier project',
              Icon: Add,
              action: SettingsActionsButtonActions.ADD_CARRIER,
              permission: {
                action: Permissions.carriers.create,
                projectID: selectedNode.entityId
              },
              availableUiFeatures: ['internal-groundfloor']
            }
          ]
        : []),
      ...(capabilities.canHaveChildProjects
        ? [
            {
              title: 'Add child project',
              Icon: Add,
              action: SettingsActionsButtonActions.CREATE_PROJECT,
              permission: {
                action: Permissions.projects.create,
                projectID: selectedNode.entityId
              },
              availableUiFeatures: ['internal-groundfloor']
            }
          ]
        : []),
      ...(capabilities.canHaveHubs
        ? [
            {
              title: 'Add child hub',
              Icon: Add,
              action: SettingsActionsButtonActions.ADD_HUB,
              permission: {
                action: Permissions.hubs.create,
                projectID: selectedNode.entityId
              },
              availableUiFeatures: ['internal-groundfloor']
            }
          ]
        : []),
      ...(capabilities.canHaveAccessControl
        ? [
            {
              title: 'Add access control',
              Icon: () => <LockOutlined style={{ width: 24, height: 24, color: '#848484' }} />,
              action: SettingsActionsButtonActions.ADD_ACCESS_CONTROL,
              permission: {
                action: Permissions.settings.updateBasic,
                projectID: selectedNode.entityId
              },
              availableUiFeatures: ['internal-groundfloor']
            }
          ]
        : []),
      ...(capabilities.canHaveRegistrationPages
        ? [
            {
              title: 'Add registration page',
              Icon: AddRegistrationPage,
              action: SettingsActionsButtonActions.ADD_REGISTRATION_PAGE,
              permission: {
                action: Permissions.settings.updateBasic,
                projectID: selectedNode.entityId
              },
              availableUiFeatures: ['internal-groundfloor']
            }
          ]
        : []),
      {
        title: 'Delete project',
        Icon: () => (
          <Deleted
            style={{
              width: 18,
              height: 18,
              margin: 4,
              color: '#848484'
            }}
          />
        ),
        action: SettingsActionsButtonActions.DELETE_PROJECT,
        permission: {
          action: Permissions.projects.delete,
          projectID: selectedNode.entityId
        },
        availableUiFeatures: ['internal-groundfloor']
      }
    ];
  }

  if (selectedNode.type === KnownSettingsNodeDtoType.ProjectModulesFolder) {
    return [
      {
        title: 'Add module',
        Icon: Add,
        action: SettingsActionsButtonActions.ADD_PROJECT_MODULE,
        permission: {
          action: Permissions.settings.updateBasic,
          projectID: selectedNode.request.rootId
        },
        availableUiFeatures: ['internal-groundfloor']
      }
    ];
  }

  if (selectedNode.type === KnownSettingsNodeDtoType.ProjectModule) {
    return [
      {
        title: 'Delete module',
        Icon: () => (
          <Deleted
            style={{
              width: 18,
              height: 18,
              margin: 4,
              color: '#848484'
            }}
          />
        ),
        action: SettingsActionsButtonActions.DELETE_PROJECT_MODULE,
        permission: {
          action: Permissions.settings.updateBasic,
          projectID: selectedNode.request.rootId
        },
        availableUiFeatures: ['internal-groundfloor']
      }
    ];
  }

  if (selectedNode.type === KnownSettingsNodeDtoType.RegistrationPage) {
    return [
      {
        title: 'Open registration page',
        Icon: () => (
          <RegistrationPageLink
            style={{
              width: 18,
              height: 18,
              margin: 4,
              color: '#848484'
            }}
          />
        ),
        action: SettingsActionsButtonActions.OPEN_REGISTRATION_PAGE,
        availableUiFeatures: ['internal-groundfloor']
      },
      {
        title: 'Delete registration page',
        Icon: () => (
          <Deleted
            style={{
              width: 18,
              height: 18,
              margin: 4,
              color: '#848484'
            }}
          />
        ),
        action: SettingsActionsButtonActions.DELETE_REGISTRATION_PAGE,
        permission: {
          action: Permissions.settings.updateBasic,
          projectID: selectedNode.request.rootId
        },
        availableUiFeatures: ['internal-groundfloor']
      }
    ];
  }

  if (selectedNode.type === KnownSettingsNodeDtoType.Hub) {
    const iconStyle = { width: 18, height: 18, margin: 4, color: '#848484' };
    return [
      {
        title: 'Add access control',
        Icon: Add,
        action: SettingsActionsButtonActions.ADD_ACCESS_CONTROL,
        permission: { action: Permissions.settings.updateBasic, projectID: selectedNode.request.rootId },
        availableUiFeatures: ['internal-groundfloor']
      },
      {
        title: 'Set accessed via',
        Icon: LinkIcon,
        action: SettingsActionsButtonActions.ADD_NEW_RELATIONSHIP,
        payload: RelationshipType.AccessedVia,
        permission: { action: Permissions.hubs.update, projectID: selectedNode.request.rootId },
        availableUiFeatures: ['internal-groundfloor']
      },
      {
        title: 'Delete hub',
        Icon: () => <ActionDelete style={iconStyle} />,
        action: SettingsActionsButtonActions.DELETE_HUB,
        permission: { action: Permissions.hubs.delete, projectID: selectedNode.request.rootId },
        availableUiFeatures: ['experimental']
      }
    ];
  }

  // Carriers only, as the endpoint refuses a container. Approval is a field on the form.
  if (selectedNode.type === KnownSettingsNodeDtoType.Group) {
    if (
      carrierGroupVariant(selectedNode.subType) !== 'unit' ||
      selectedNode.status === KnownSettingsNodeDtoStatus.Deleted
    ) {
      return [];
    }
    return [
      {
        title: 'Delete carrier',
        Icon: () => <ActionDelete style={{ width: 18, height: 18, margin: 4, color: '#848484' }} />,
        action: SettingsActionsButtonActions.DELETE_CARRIER,
        permission: { action: Permissions.groups.delete, projectID: selectedNode.request.rootId },
        availableUiFeatures: ['internal-groundfloor']
      }
    ];
  }

  if (selectedNode.type === KnownSettingsNodeDtoType.KioskTheme) {
    return [
      {
        title: 'Apply preset',
        Icon: Add,
        action: SettingsActionsButtonActions.APPLY_KIOSK_THEME_PRESET,
        permission: { action: Permissions.settings.updateBasic, projectID: selectedNode.request.rootId },
        availableUiFeatures: ['internal-groundfloor']
      }
    ];
  }

  if (selectedNode.type === KnownSettingsNodeDtoType.AccessControl) {
    if (selectedNode.status === KnownSettingsNodeDtoStatus.Deleted) {
      return [];
    }
    return [
      {
        title: 'Delete access control',
        Icon: () => (
          <Deleted
            style={{
              width: 18,
              height: 18,
              margin: 4,
              color: '#848484'
            }}
          />
        ),
        action: SettingsActionsButtonActions.DELETE_ACCESS_CONTROL,
        permission: {
          action: Permissions.settings.updateBasic,
          projectID: selectedNode.request.rootId
        },
        availableUiFeatures: ['internal-groundfloor']
      }
    ];
  }

  if (selectedNode.type === KnownSettingsNodeDtoType.HubLayout) {
    // No actions available for hub layout nodes
    return [];
  }

  if (selectedNode.type === KnownSettingsNodeDtoType.Tower) {
    // No actions available for tower nodes
    return [];
  }

  return [];
}

function validationAction(
  selectedNode?: EnrichedSettingsNodeDto,
  registrationPageExists?: boolean,
  accessControlExists?: boolean
) {
  return function validateAction(action: SettingsActionsButtonActions) {
    if (!selectedNode) {
      return false;
    }

    switch (action) {
      case SettingsActionsButtonActions.ADD_REGISTRATION_PAGE:
        return !registrationPageExists;
      case SettingsActionsButtonActions.ADD_ACCESS_CONTROL:
        return !accessControlExists;
      default:
        return true;
    }
  };
}

export function makeDefineSettingsActionsButtonActions(
  selectedNode?: EnrichedSettingsNodeDto,
  projectChildrenSettingsTree?: EnrichedSettingsTreeDto,
  registrationPageExists?: boolean,
  accessControlExists?: boolean,
  capabilities?: ProjectCapabilities
) {
  return makeDefineActions(
    makeSettingsActionsButtonActions(selectedNode, projectChildrenSettingsTree, capabilities),
    undefined,
    validationAction(selectedNode, registrationPageExists, accessControlExists)
  );
}
